// src/ConsumerApp/DesignSystem/Navigation/Navbar.js

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { theme } from '../../../assets/theme/theme';
import bucket from '../../../assets/icons/bucketConcrete.svg';
import UserCircle from '../../../assets/icons/UserCircle.svg';
import MagnifyingGlass from '../../../assets/icons/MagnifyingGlass.svg';
import CenteredContainer from '../Layout/CenteredContainer';
import DeliveryAddressModal from '../../Screens/Addresses/DeliveryAddressModal';
import Bucket from '../../Screens/Bucket/Bucket';
import CartFlyoutPopover from './components/CartFlyoutPopover';

import { useGetBucketSummary } from '../../api/products/product.hooks';
import { useGetDefaultDeliveryAddress } from '../../api/projects/project.hooks';
import { useAuthModal } from '../../context/AuthModalContext';
import { guestBucketManager } from '../../utils/guestBucketManager';

const ICON_WITH_TEXT_LOGO = `${process.env.PUBLIC_URL || ''}/iconwithtext.png`;

const SEARCH_PLACEHOLDERS = [
  'Search "Ultratech Cement"',
  'Search "Finolex PVC Pipes"',
  'Search "Asian Paints Primer"',
  'Search "Polycab Wires"',
  'Search "TMT Steel Bars"',
  'Search "Dr. Fixit Waterproofing"',
];

const normalizeId = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  return String(
    raw._id ||
    raw.id ||
    raw.product_id?._id ||
    raw.product_id ||
    raw.item_id?._id ||
    raw.item_id ||
    ''
  );
};

export default function Navbar({ searchQuery = '', onSearchChange, onVoiceSearchClick }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { openLoginModal } = useAuthModal();

  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isBucketOpen, setIsBucketOpen] = useState(false);
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const [resetTimerSignal, setResetTimerSignal] = useState(0);

  // 1. Reactive customer authentication state
  const [customer, setCustomer] = useState(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // 2. Reactive guest cart count
  const [guestCount, setGuestCount] = useState(() => guestBucketManager.getTotalCount());

  // 3. Placeholder animation
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % SEARCH_PLACEHOLDERS.length);
    }, 2800);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleAuthChange = () => {
      try {
        const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
        const parsed = stored ? JSON.parse(stored) : null;
        setCustomer(parsed);
        if (parsed?._id || parsed?.id) {
          guestBucketManager.clearGuestBucket();
        }
      } catch {
        setCustomer(null);
      }
    };

    const handleGuestBucketChange = () => {
      setGuestCount(guestBucketManager.getTotalCount());
    };

    window.addEventListener('auth-state-change', handleAuthChange);
    window.addEventListener('storage', handleAuthChange);
    window.addEventListener('guest-bucket-change', handleGuestBucketChange);

    return () => {
      window.removeEventListener('auth-state-change', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
      window.removeEventListener('guest-bucket-change', handleGuestBucketChange);
    };
  }, []);

  const customerId = customer?._id || customer?.id;

  // Server queries
  const { data: addressRes } = useGetDefaultDeliveryAddress(customerId);
  const { data: bucketSummary, refetch: refetchBucket } = useGetBucketSummary(customerId);

  const cartItemCount = customerId
    ? bucketSummary?.buy_now_bucket?.items_count || 0
    : guestCount;

  // Real-time live overrides map for quantity adjustments
  const [liveOverrides, setLiveOverrides] = useState({});

  useEffect(() => {
    const handleCartItemAdded = (e) => {
      const { item, quantity, productId } = e.detail || {};
      const targetId = normalizeId(productId || item);

      if (targetId) {
        const itemName =
          item?.item_name ||
          item?.product_name ||
          item?.name ||
          'Building Material';

        const itemImg =
          item?.item_image ||
          item?.product_image_url ||
          item?.product_image ||
          item?.image;

        const itemPrice = Number(
          item?.buy_now?.unit_price ||
          item?.selling_price ||
          item?.price_per_unit ||
          item?.unit_price ||
          0
        );

        setLiveOverrides((prev) => ({
          ...prev,
          [targetId]: {
            quantity: Number(quantity) || (prev[targetId]?.quantity ? prev[targetId].quantity + 1 : 1),
            price: itemPrice > 0 ? itemPrice : (prev[targetId]?.price || 0),
            name: itemName,
            image: itemImg || prev[targetId]?.image,
          },
        }));
      }

      if (customerId && refetchBucket) {
        refetchBucket();
      }

      if (typeof window !== 'undefined' && window.innerWidth > 768 && !isBucketOpen) {
        setIsFlyoutOpen(true);
        setResetTimerSignal((prev) => prev + 1);
      }
    };

    const handleCartItemUpdated = (e) => {
      const { item, quantity, productId } = e.detail || {};
      const targetId = normalizeId(productId || item);
      const newQty = Number(quantity) || 0;

      if (newQty <= 0) {
        setLiveOverrides((prev) => {
          const copy = { ...prev };
          delete copy[targetId];
          return copy;
        });
      } else if (targetId) {
        setLiveOverrides((prev) => ({
          ...prev,
          [targetId]: {
            ...prev[targetId],
            quantity: newQty,
          },
        }));
        setResetTimerSignal((prev) => prev + 1);
      }

      if (customerId && refetchBucket) {
        refetchBucket();
      }
    };

    const handleCartItemRemoved = (e) => {
      const { productId } = e.detail || {};
      const targetId = normalizeId(productId);

      if (targetId) {
        setLiveOverrides((prev) => {
          const copy = { ...prev };
          delete copy[targetId];
          return copy;
        });
      }

      if (customerId && refetchBucket) {
        refetchBucket();
      }
    };

    window.addEventListener('cart-item-added', handleCartItemAdded);
    window.addEventListener('cart-item-updated', handleCartItemUpdated);
    window.addEventListener('cart-item-removed', handleCartItemRemoved);

    return () => {
      window.removeEventListener('cart-item-added', handleCartItemAdded);
      window.removeEventListener('cart-item-updated', handleCartItemUpdated);
      window.removeEventListener('cart-item-removed', handleCartItemRemoved);
    };
  }, [isBucketOpen, customerId, refetchBucket]);

  // Clean data merger matching your backend summary payload
  const flyoutItems = useMemo(() => {
    let baseList = [];

    if (customerId) {
      // Exactly matches the backend JSON: buy_now_bucket.items
      const serverItems = bucketSummary?.buy_now_bucket?.items || [];
      baseList = serverItems
        .filter((sItem) => Number(sItem.quantity || 0) > 0)
        .map((sItem) => ({
          item_id: String(sItem.item_id),
          item_name: sItem.item_name || 'Building Material',
          unit_price: 0, // Not provided by summary endpoint; flyout hides 0
          quantity: Number(sItem.quantity || 1),
          item_image: sItem.item_image,
        }));
    } else {
      const guestItems = guestBucketManager.getItems() || [];
      baseList = guestItems
        .filter((gItem) => Number(gItem.quantity || 0) > 0)
        .map((gItem) => ({
          item_id: normalizeId(gItem.product_id || gItem._id || gItem.id || gItem),
          item_name: gItem.product_name || gItem.name || 'Building Material',
          unit_price: Number(gItem.buy_now?.unit_price || gItem.selling_price || gItem.price || 0),
          quantity: Number(gItem.quantity || 1),
          item_image: gItem.product_image_url || gItem.product_image || gItem.image,
        }));
    }

    const resultList = [...baseList];

    // Apply live overrides so newly clicked items & updated quantities reflect immediately
    Object.entries(liveOverrides).forEach(([overrideId, overrideData]) => {
      const targetId = normalizeId(overrideId);
      const targetQty = Number(overrideData.quantity) || 0;

      if (targetQty <= 0) {
        const idx = resultList.findIndex((item) => normalizeId(item.item_id) === targetId);
        if (idx >= 0) resultList.splice(idx, 1);
        return;
      }

      const index = resultList.findIndex((item) => normalizeId(item.item_id) === targetId);
      if (index >= 0) {
        resultList[index] = {
          ...resultList[index],
          quantity: targetQty,
          unit_price: overrideData.price > 0 ? overrideData.price : resultList[index].unit_price,
        };
      } else {
        resultList.push({
          item_id: targetId,
          item_name: overrideData.name || 'Building Material',
          unit_price: Number(overrideData.price) || 0,
          quantity: targetQty,
          item_image: overrideData.image,
        });
      }
    });

    return resultList.filter((item) => item.quantity > 0);
  }, [customerId, bucketSummary, guestCount, liveOverrides]);

  useEffect(() => {
    if (isBucketOpen || flyoutItems.length === 0) {
      setIsFlyoutOpen(false);
    }
  }, [isBucketOpen, flyoutItems.length]);

  const { projectName, addressLocality, fullTooltip } = useMemo(() => {
    const addr = addressRes?.data?.address || addressRes?.data || addressRes?.address || addressRes;
    if (!addr) {
      return {
        projectName: '',
        addressLocality: customerId ? 'Select site delivery address' : 'Select delivery location',
        fullTooltip: 'Select delivery address',
      };
    }

    const pName = addr.project_name ? addr.project_name.trim() : '';
    const addressParts = [
      addr.area_sector_locality || addr.flat_number || addr.building_name,
      addr.city,
    ].filter(Boolean);

    const locality = addressParts.length > 0
      ? addressParts.join(', ')
      : (addr.complete_address || 'Current Site');

    return {
      projectName: pName,
      addressLocality: locality,
      fullTooltip: pName ? `${pName} - ${locality}` : locality,
    };
  }, [addressRes, customerId]);

  const handleSearchInteraction = () => {
    if (location.pathname !== '/consumer/search') {
      const targetQuery = searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : '';
      navigate(`/consumer/search${targetQuery}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const queryParam = e.target.value.trim();
      navigate(`/consumer/search?q=${encodeURIComponent(queryParam)}`);
    }
  };

  const isProductDetailsPage = location.pathname.toLowerCase().includes('productdetails');

  return (
    <>
      <style>{`
        .bb-navbar-shell {
          background-color: #FFFFFF;
          border-bottom: 1px solid #E2E8F0;
          position: sticky;
          top: 0;
          z-index: 1000;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
          width: 100%;
        }

        .bb-nav-desktop-layout {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          height: 76px;
        }

        .bb-nav-left-cluster {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-shrink: 0;
        }

        .bb-nav-brand-logo {
          height: 48px;
          max-height: 52px;
          width: auto;
          cursor: pointer;
          display: block;
          object-fit: contain;
        }

        .bb-nav-divider {
          width: 1.5px;
          height: 32px;
          background-color: #E2E8F0;
          border-radius: 999px;
          align-self: center;
          flex-shrink: 0;
        }

        .bb-nav-delivery-box {
          display: flex;
          flex-direction: column;
          cursor: pointer;
          max-width: 250px;
        }

        .bb-nav-eta-badge {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .bb-nav-eta-text {
          font-family: ${theme.typography.headingMD.fontFamily};
          font-size: 13.5px;
          font-weight: 800;
          color: #2B2A29;
          letter-spacing: -0.2px;
        }

        .bb-nav-address-line {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-top: 1px;
          overflow: hidden;
          white-space: nowrap;
        }

        .bb-nav-project-text {
          font-size: 12px;
          font-weight: 700;
          color: #2B2A29;
          max-width: 110px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .bb-nav-locality-text {
          font-size: 11.5px;
          color: #64748B;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 130px;
        }

        .bb-nav-search-bar {
          flex: 1;
          max-width: 580px;
          min-width: 280px;
          display: flex;
          align-items: center;
          background-color: #F8FAFC;
          border: 1.5px solid #E2E8F0;
          border-radius: 12px;
          padding: 10px 16px;
          gap: 10px;
          cursor: text;
          box-sizing: border-box;
          transition: border-color 0.15s ease, background-color 0.15s ease;
        }

        .bb-nav-search-bar:focus-within {
          border-color: #CBD5E1;
          background-color: #FFFFFF;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .bb-nav-search-input {
          flex: 1;
          border: none;
          outline: none;
          background: transparent;
          font-size: 13.5px;
          font-weight: 500;
          color: #2B2A29;
          padding: 0;
        }

        .bb-nav-right-cluster {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-shrink: 0;
        }

        .bb-nav-cart-btn-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .bb-nav-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 8px;
          outline: none;
          user-select: none;
        }

        .bb-nav-btn-icon-wrap {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
        }

        .bb-nav-btn-label {
          font-size: 11px;
          font-weight: 700;
          color: #2B2A29;
          margin-top: 2px;
        }

        .bb-nav-cart-badge {
          position: absolute;
          top: -4px;
          right: -7px;
          background-color: #E9001E;
          color: #FFFFFF;
          font-size: 10px;
          font-weight: 800;
          border-radius: 999px;
          min-width: 17px;
          height: 17px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 4px;
          box-shadow: 0 2px 4px rgba(233, 0, 30, 0.35);
        }

        .bb-nav-mobile-layout {
          display: none;
        }

        .bb-mobile-floating-cart {
          display: none;
        }

        @media (max-width: 768px) {
          .bb-nav-desktop-layout {
            display: none !important;
          }

          .bb-navbar-shell > div {
            padding-left: 0 !important;
            padding-right: 0 !important;
            max-width: 100% !important;
          }

          .bb-navbar-shell {
            background-color: #FFFFFF;
            border-bottom: 1px solid #F1F5F9;
            padding: 8px 12px 10px 12px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
          }

          .bb-nav-mobile-layout {
            display: flex;
            flex-direction: column;
            gap: 8px;
            width: 100%;
          }

          .bb-mob-top-bar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
          }

          .bb-mob-location-target {
            display: flex;
            flex-direction: column;
            cursor: pointer;
            flex: 1;
            min-width: 0;
            padding-right: 8px;
          }

          .bb-mob-eta-heading {
            display: flex;
            align-items: center;
            gap: 4px;
          }

          .bb-mob-flash-icon {
            font-size: 15px;
            color: #D97706;
            line-height: 1;
          }

          .bb-mob-eta-title {
            font-size: 16px;
            font-weight: 900;
            color: #0F172A;
            letter-spacing: -0.3px;
            line-height: 19px;
          }

          .bb-mob-address-line {
            display: flex;
            align-items: center;
            gap: 4px;
            margin-top: 1px;
            overflow: hidden;
            white-space: nowrap;
          }

          .bb-mob-site-tag {
            font-size: 12px;
            font-weight: 700;
            color: #1E293B;
            max-width: 130px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .bb-mob-site-sub {
            font-size: 12px;
            font-weight: 500;
            color: #475569;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            flex: 1;
          }

          .bb-mob-caret-down {
            width: 12px;
            height: 12px;
            flex-shrink: 0;
            color: #1E293B;
          }

          .bb-mob-profile-btn {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            background-color: #F8FAFC;
            border: 1px solid #E2E8F0;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            cursor: pointer;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
            transition: transform 0.15s ease;
          }

          .bb-mob-profile-btn:active {
            transform: scale(0.95);
          }

          .bb-mob-search-container {
            width: 100%;
            display: flex;
            align-items: center;
            background-color: #F8FAFC;
            border: 1.5px solid #E2E8F0;
            border-radius: 12px;
            padding: 8px 12px;
            gap: 8px;
            box-sizing: border-box;
            box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.02);
            height: 42px;
            cursor: text;
          }

          .bb-mob-search-container:focus-within {
            background-color: #FFFFFF;
            border-color: #CBD5E1;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
          }

          .bb-mob-search-lens {
            width: 16px;
            height: 16px;
            color: #64748B;
            flex-shrink: 0;
          }

          .bb-mob-search-input {
            flex: 1;
            border: none;
            outline: none;
            font-size: 13px;
            font-weight: 600;
            color: #0F172A;
            background: transparent;
            padding: 0;
            line-height: 18px;
          }

          .bb-mob-search-input::placeholder {
            color: #64748B;
            font-weight: 500;
          }

          .bb-mob-mic-btn {
            background: none;
            border: none;
            cursor: pointer;
            font-size: 14px;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0.8;
          }

          .bb-mobile-floating-cart {
            display: flex;
            position: fixed;
            bottom: 14px;
            left: 12px;
            right: 12px;
            background: linear-gradient(135deg, #E9001E 0%, #C7061F 100%);
            color: #FFFFFF;
            border-radius: 14px;
            padding: 12px 16px;
            align-items: center;
            justify-content: space-between;
            box-shadow: 0 10px 24px rgba(233, 0, 30, 0.42);
            z-index: 9999;
            cursor: pointer;
            box-sizing: border-box;
            animation: slideUpCart 0.22s cubic-bezier(0.16, 1, 0.3, 1);
          }

          @keyframes slideUpCart {
            from {
              opacity: 0;
              transform: translateY(24px) scale(0.97);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          .bb-mob-cart-left {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .bb-mob-cart-badge-circle {
            background-color: #FFFFFF;
            color: #E9001E;
            font-size: 12px;
            font-weight: 900;
            width: 24px;
            height: 24px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .bb-mob-cart-title {
            font-size: 13.5px;
            font-weight: 800;
            color: #FFFFFF;
            letter-spacing: 0.1px;
          }

          .bb-mob-cart-right {
            display: flex;
            align-items: center;
            gap: 5px;
            font-size: 13px;
            font-weight: 700;
            color: #FFFFFF;
          }
        }

        @media (max-width: 380px) {
          .bb-navbar-shell {
            padding: 6px 10px 8px 10px;
          }
          .bb-mobile-floating-cart {
            left: 10px;
            right: 10px;
          }
        }
      `}</style>

      <header className="bb-navbar-shell">
        <CenteredContainer>
          <div className="bb-nav-desktop-layout">
            <div className="bb-nav-left-cluster">
              <img
                src={ICON_WITH_TEXT_LOGO}
                alt="Brickbucket"
                className="bb-nav-brand-logo"
                onClick={() => navigate('/consumer/HomeScreen')}
              />

              <div className="bb-nav-divider" />

              <div
                className="bb-nav-delivery-box"
                onClick={() => {
                  if (customerId) setIsAddressModalOpen(true);
                  else openLoginModal();
                }}
              >
                <div className="bb-nav-eta-badge">
                  <span style={{ fontSize: 13 }}>⚡</span>
                  <span className="bb-nav-eta-text">Delivery in 30-90 mins</span>
                </div>

                <div className="bb-nav-address-line" title={fullTooltip}>
                  {projectName && (
                    <>
                      <span className="bb-nav-project-text">{projectName}</span>
                      <span style={{ color: '#94A3B8' }}>-</span>
                    </>
                  )}
                  <span className="bb-nav-locality-text">{addressLocality}</span>
                  <span style={{ fontSize: 9, color: '#64748B', marginLeft: 2 }}>▼</span>
                </div>
              </div>
            </div>

            <div className="bb-nav-search-bar" onClick={handleSearchInteraction}>
              <img src={MagnifyingGlass} alt="search" style={{ width: 16, height: 16, opacity: 0.55 }} />
              <input
                type="text"
                placeholder={SEARCH_PLACEHOLDERS[placeholderIndex]}
                value={searchQuery}
                onClick={handleSearchInteraction}
                onFocus={handleSearchInteraction}
                onKeyDown={handleKeyDown}
                onChange={(e) => {
                  if (location.pathname !== '/consumer/search') {
                    navigate(`/consumer/search?q=${encodeURIComponent(e.target.value)}`);
                  } else if (onSearchChange) {
                    onSearchChange(e.target.value);
                  }
                }}
                className="bb-nav-search-input"
              />
              {onVoiceSearchClick && (
                <button
                  type="button"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onVoiceSearchClick();
                  }}
                >
                  🎤
                </button>
              )}
            </div>

            <div className="bb-nav-right-cluster">
              {customerId ? (
                <button
                  type="button"
                  className="bb-nav-btn"
                  onClick={() => navigate('/consumer/account')}
                  title="My Account"
                >
                  <div className="bb-nav-btn-icon-wrap">
                    <img src={UserCircle} alt="Profile" style={{ width: 26, height: 26 }} />
                  </div>
                  <span className="bb-nav-btn-label">Profile</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="bb-nav-btn"
                  onClick={openLoginModal}
                  title="Login to Account"
                >
                  <div className="bb-nav-btn-icon-wrap">
                    <img src={UserCircle} alt="Login" style={{ width: 26, height: 26 }} />
                  </div>
                  <span className="bb-nav-btn-label">Login</span>
                </button>
              )}

              <div className="bb-nav-cart-btn-wrapper">
                <button
                  type="button"
                  className="bb-nav-btn"
                  onClick={() => {
                    setIsFlyoutOpen(false);
                    setIsBucketOpen(true);
                  }}
                  title="View Bucket"
                >
                  <div className="bb-nav-btn-icon-wrap">
                    <img src={bucket} alt="Bucket" style={{ width: 26, height: 26 }} />
                    {cartItemCount > 0 && <span className="bb-nav-cart-badge">{cartItemCount}</span>}
                  </div>
                  <span className="bb-nav-btn-label">Bucket</span>
                </button>

                <CartFlyoutPopover
                  isOpen={isFlyoutOpen}
                  onClose={() => setIsFlyoutOpen(false)}
                  items={flyoutItems}
                  resetTimerSignal={resetTimerSignal}
                  onGoToCart={() => {
                    setIsFlyoutOpen(false);
                    setIsBucketOpen(true);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="bb-nav-mobile-layout">
            <div className="bb-mob-top-bar">
              <div
                className="bb-mob-location-target"
                onClick={() => {
                  if (customerId) setIsAddressModalOpen(true);
                  else openLoginModal();
                }}
              >
                <div className="bb-mob-eta-heading">
                  <span className="bb-mob-flash-icon">⚡</span>
                  <span className="bb-mob-eta-title">Delivery in 30–90 mins</span>
                </div>

                <div className="bb-mob-address-line">
                  {projectName && <span className="bb-mob-site-tag">{projectName} -</span>}
                  <span className="bb-mob-site-sub" title={fullTooltip}>
                    {addressLocality}
                  </span>
                  <svg className="bb-mob-caret-down" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>

              <button
                type="button"
                className="bb-mob-profile-btn"
                onClick={() => {
                  if (customerId) navigate('/consumer/account');
                  else openLoginModal();
                }}
                aria-label="Account profile"
              >
                <img src={UserCircle} alt="Profile" style={{ width: 22, height: 22 }} />
              </button>
            </div>

            <div className="bb-mob-search-container" onClick={handleSearchInteraction}>
              <svg className="bb-mob-search-lens" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>

              <input
                type="text"
                placeholder={SEARCH_PLACEHOLDERS[placeholderIndex]}
                value={searchQuery}
                onClick={handleSearchInteraction}
                onFocus={handleSearchInteraction}
                onKeyDown={handleKeyDown}
                onChange={(e) => {
                  if (location.pathname !== '/consumer/search') {
                    navigate(`/consumer/search?q=${encodeURIComponent(e.target.value)}`);
                  } else if (onSearchChange) {
                    onSearchChange(e.target.value);
                  }
                }}
                className="bb-mob-search-input"
              />

              {onVoiceSearchClick && (
                <button
                  type="button"
                  className="bb-mob-mic-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onVoiceSearchClick();
                  }}
                  title="Voice Search"
                >
                  🎤
                </button>
              )}
            </div>
          </div>
        </CenteredContainer>
      </header>

      {cartItemCount > 0 && !isProductDetailsPage && (
        <div
          className="bb-mobile-floating-cart"
          onClick={() => setIsBucketOpen(true)}
        >
          <div className="bb-mob-cart-left">
            <div className="bb-mob-cart-badge-circle">{cartItemCount}</div>
            <span className="bb-mob-cart-title">
              {cartItemCount === 1 ? 'Item in Bucket' : `Items in Bucket`}
            </span>
          </div>

          <div className="bb-mob-cart-right">
            <span>View Bucket</span>
            <span>→</span>
          </div>
        </div>
      )}

      {customerId && (
        <DeliveryAddressModal
          isOpen={isAddressModalOpen}
          onClose={() => setIsAddressModalOpen(false)}
          onAddNewAddress={(loc) => navigate('/consumer/account/delivery/new', { state: loc })}
        />
      )}

      <Bucket isOpen={isBucketOpen} onClose={() => setIsBucketOpen(false)} />
    </>
  );
}